import { useMemo, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { CatInputField } from '@blocks-react/components/InputField';
import { useFormContext, Controller } from 'react-hook-form';
import MobileDropdownDrawer from '@app/components/common/MobileDropdownDrawer';
import { getCountryObjectFromCallingCode } from '../../../address/utils';
import { isEmpty } from '@app/utils/index';
import { replaceTokensInString } from '@app/utils';
import { PCC_CHECKOUT_PAGES_DRAWERS_MOBILE } from '@app/constants/featureFlags';
import { googlePhoneValidate } from '../../../address/googleUtils';
import cx from 'classnames';
import styles from '../AddressFormFields.module.scss';

const PhoneNumberInput = ({
  fieldId,
  handleValueFromSessionStorage = false,
  valuefromSessionStorage = {},
  showRequired: isPhoneRequired,
  selectedCountry
}) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors }
  } = useFormContext();
  const countryDataSelector = useSelector(state => state.dealer?.countryInfo);
  const checkoutPagesDrawersMobileFlag = useSelector(
    state => state.featureFlag[PCC_CHECKOUT_PAGES_DRAWERS_MOBILE]
  );

  const countryData = useMemo(
    () =>
      countryDataSelector
        ? countryDataSelector.filter(
            countryObject => !!countryObject.callingCode
          )
        : [],
    [countryDataSelector]
  );

  const phone = getValues(fieldId);
  const [selectedCountryObject, setSelectedCountryObject] = useState(null);

  useEffect(() => {
    if (
      !isEmpty(phone) &&
      isEmpty(selectedCountryObject) &&
      !isEmpty(selectedCountry)
    ) {
      const extractedCallingCodeArray = phone.match(/\+\d+/);
      const extractedCallingCode =
        extractedCallingCodeArray && extractedCallingCodeArray[0];
      const currentPhoneWithoutCountryCode = phone.replace(/\+\d+ +/, '');

      const currentCountryCallingCodeObject = getCountryObjectFromCallingCode({
        countryData,
        propCallingCode: extractedCallingCode,
        userCountry: selectedCountry
      });

      const phoneWithCallingCode = `${currentCountryCallingCodeObject?.callingCode} ${currentPhoneWithoutCountryCode}`;

      setSelectedCountryObject(currentCountryCallingCodeObject);
      setValue(fieldId, phoneWithCallingCode);
      setValue('phoneCode', currentCountryCallingCodeObject.code);
    } else if (!isEmpty(phone) && !isEmpty(selectedCountryObject)) {
      const currentPhoneWithoutCountryCode = phone.replace(/\+\d+ +/, '');
      const phoneWithCallingCode = `${selectedCountryObject?.callingCode} ${currentPhoneWithoutCountryCode}`;
      setValue(fieldId, phoneWithCallingCode);
      setValue('phoneCode', selectedCountryObject?.code);
    } else if (isEmpty(selectedCountryObject)) {
      const countryObject = handleValueFromSessionStorage
        ? valuefromSessionStorage
        : getCountryObjectFromCallingCode({
            countryData,
            userCountry: selectedCountry
          });
      setSelectedCountryObject(countryObject);
      setValue('phoneCode', countryObject.code);
    }
  }, [
    selectedCountry,
    countryData,
    getValues,
    handleValueFromSessionStorage,
    phone,
    setValue,
    valuefromSessionStorage,
    selectedCountryObject,
    fieldId,
    trigger
  ]);

  const onBlChange = e => {
    setValue(fieldId, e.detail?.value);
  };

  const onSelectHandler = value => {
    const selectedCountry = countryData.find(country => country.code === value);
    setSelectedCountryObject(selectedCountry);
  };

  const getPhoneValidator = useCallback(
    currentValue => {
      if (!isPhoneRequired && isEmpty(currentValue)) {
        return;
      }

      if (isPhoneRequired && isEmpty(currentValue)) {
        return replaceTokensInString(
          t('1626_FIELD_REQUIRED_ERROR'),
          t('CAT_EMAIL_PHONE1')
        );
      }

      const filteredPhone = currentValue.replace(/[^\d +()-]/g, '');
      if (filteredPhone.legth === 1) {
        return t('ERROR_INVALIDPHONE2');
      }

      return googlePhoneValidate(
        fieldId,
        t('ERROR_INVALIDPHONE2'),
        filteredPhone,
        selectedCountryObject,
        false,
        setValue,
        null
      );
    },
    [isPhoneRequired, fieldId, t, selectedCountryObject, setValue]
  );

  const countryCodes = countryData.map(({ code }) => code).sort();

  return (
    <div className="row align-items-end">
      <div className="d-flex gap-1">
        <span
          className={cx(
            'cat-u-theme-typography-label-sm',
            styles['address-form-fields__phone_label']
          )}
        >
          {t('CAT_EMAIL_PHONE1')}
        </span>
        {isPhoneRequired && (
          <span
            className={cx(
              'cat-u-theme-typography-footnote',
              styles['address-form-fields__phone_label-required']
            )}
          >
            {t('REQUIRED_FIELD_MESSAGE')}
          </span>
        )}
      </div>
      <div className="col-4 pe-2">
        <MobileDropdownDrawer
          fieldId="phoneCode"
          items={countryCodes}
          drawerTitle={t('COUNTRY_CODE')}
          additional={onSelectHandler}
          searchPlaceHolder={t('SEARCH_COUNTRY_CODE')}
          withSearch={checkoutPagesDrawersMobileFlag}
        />
      </div>
      <div className="col-8 ps-0">
        <Controller
          control={control}
          name={fieldId}
          rules={{
            validate: { getPhoneValidator }
          }}
          render={({ field }) => (
            <CatInputField
              {...field}
              size="md"
              fieldId={fieldId}
              name={fieldId}
              data-testid={fieldId}
              hideLabel
              type="tel"
              onBlChange={onBlChange}
              status={errors?.[fieldId] ? 'error' : undefined}
              onBlur={() => trigger(fieldId)}
            />
          )}
        />
      </div>
      {errors?.[fieldId] && (
        <div>
          <span
            className={cx(
              'cat-u-theme-typography-footnote',
              styles['address-form-fields__phone_label-error']
            )}
          >
            {errors?.[fieldId]?.message}
          </span>
        </div>
      )}
    </div>
  );
};
PhoneNumberInput.propTypes = {
  fieldId: PropTypes.string,
  showRequired: PropTypes.bool,
  handleValueFromSessionStorage: PropTypes.bool,
  valuefromSessionStorage: PropTypes.shape({}),
  selectedCountry: PropTypes.string
};

export default PhoneNumberInput;
